/**
 * TourContext (v3)
 *
 * Exposes tour state to child components. Key additions in v3:
 *   - `isTourActive` covers the entire tour lifecycle (welcome, in_progress,
 *     celebration). Use this to bypass premium gates during the whole flow.
 *   - `isTourMode` (legacy name) still works; it's an alias for isTourActive.
 *   - STEPS copy updated per the design pass.
 *
 * Usage:
 *   const { isTourActive, currentStep, stepName, skipTour } = useTour();
 */
import { createContext, useContext } from 'react';

export const TourContext = createContext({
  isTourActive: false,
  isTourMode: false, // alias
  currentStep: null,
  stepName: null,
  targetTab: null,
  skipTour: () => {},
});

export const useTour = () => useContext(TourContext);

export const STEPS = [
  {
    index: 0,
    name: 'rule',
    tab: 'rules',
    title: 'Add a no-spend rule',
    completionField: 'rules',
    coachTarget: 'addRuleBtn',
    coachTitle: 'Tap here to add a rule',
    coachBody: 'Your spending rules live on the home screen. Tap to add one.',
  },
  {
    index: 1,
    name: 'transaction',
    tab: 'budget',
    title: 'Log your first transaction',
    completionField: 'transactions',
    coachTarget: 'budgetFab',
    coachTitle: 'Tap the + to log a transaction',
    coachBody: 'This button always takes you to the Add transaction menu.',
  },
  {
    index: 2,
    name: 'calendarDay',
    tab: 'calendar',
    // The step completes on ANY calendar day log (no-spend, spend, or neutral).
    // We still nudge users toward no-spend because that's the habit-building one.
    title: 'Log a day on the calendar',
    completionField: 'calendarDays', // computed as sum of 3 fields — see tourState
    coachTarget: 'todayCell',
    coachTitle: "Tap today's date",
    coachBody: 'Log your no-spend habits. Pick no-spend to mark a willpower win!',
  },
  {
    index: 3,
    name: 'wishlistItem',
    tab: 'wishlist',
    title: 'Add something to your wishlist',
    completionField: 'wishlist',
    coachTarget: 'wishlistAddCard',
    coachTitle: 'Add a wishlist item',
    coachBody: 'Enter a name and price (and optionally a photo), then tap + to save.',
  },
];
